import {PreviewFormValues} from 'components/customer/Preview3D/types';
import * as THREE from 'three';
import {getCarcaseTextures} from './advancedShelf';
import {
    applyCarcaseMaterial,
    applyWallFlushOutlineFix,
    attachWireframeToggleEvent as attachEvents,
    createLineSegments,
} from 'components/customer/Preview3DCommon/shelves/common';
import {min} from 'lodash';
import {addDragEffectOnClick} from 'components/customer/Preview3D/helpers';

interface ArcDetails {
    straightSectionX: number;
    straightSectionY: number;
    largeInternalRadiusPercentage: number;
    minPartSize: number;
    minShelfDepth: number;
    bigCircleCenterX: number;
    bigCircleCenterY: number;
    hDistance: number;
    dDistance: number;
    largeInternalRadius: number;
    smallExternalRadius: number;
}

interface Coordinates {
    x: number;
    y: number;
}

const getArcEnd = ({
    smallExternalRadius,
    dDistance,
    largeInternalRadius,
    bigCircleCenterX,
    straightSectionY,
    hDistance,
}: ArcDetails) => {
    // x and y are calculated based on woodcam's formula -- see ShKeyShapeRev01 workbook
    const x =
        bigCircleCenterX +
        dDistance +
        smallExternalRadius -
        (smallExternalRadius * (smallExternalRadius + dDistance)) /
            (largeInternalRadius + smallExternalRadius);
    const y =
        straightSectionY +
        (smallExternalRadius * hDistance) /
            (largeInternalRadius + smallExternalRadius);
    return {x, y};
};

const circleCentersFromPointsAndRadius = (
    x1: number, // x coordinate of the first point on the circle
    y1: number, // y coordinate of the first point on the circle
    x2: number, // x coordinate of the second point on the circle
    y2: number, // y coordinate of the second point on the circle
    r: number
) => {
    // Step 1: Find the midpoint of the chord defined by the two points
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;

    // Step 2: Calculate the (dx, dy) vector between points and its length (chord length)
    const dx = x2 - x1;
    const dy = y2 - y1;
    const d = Math.hypot(dx, dy); // chord length

    // Step 3: Check if a circle of the given radius can go through the two points
    if (d > 2 * r) {
        // The points are too far apart, no such circle exists
        return [];
    }

    // Step 4: Find the distance from the midpoint to each possible center (h)
    // Using the Pythagorean theorem: r^2 = (d/2)^2 + h^2 => h = sqrt(r^2 - (d/2)^2)
    const h = Math.sqrt(r * r - (d / 2) * (d / 2));

    // Step 5: Find the direction perpendicular to the chord (normalized)
    // Perpendicular vector is (-dy, dx)
    const perpX = -dy / d;
    const perpY = dx / d;

    // Step 6: The two possible centers are at (midX, midY) ± h * (perpX, perpY)
    return [
        {x: midX + perpX * h, y: midY + perpY * h},
        {x: midX - perpX * h, y: midY - perpY * h},
    ];
};

const drawArc = (
    shape: THREE.Shape,
    radius: number,
    arcCenter: Coordinates,
    arcStart: Coordinates,
    arcEnd: Coordinates,
    clockwise: boolean
) => {
    // convert start coordinate to radians
    const startAngle = Math.atan2(
        arcStart.y - arcCenter.y,
        arcStart.x - arcCenter.x
    );

    // convert end coordinate to radians
    const endAngle = Math.atan2(arcEnd.y - arcCenter.y, arcEnd.x - arcCenter.x);

    shape.absarc(
        arcCenter.x, // center x-axis of the arc
        arcCenter.y, // center y-axis of the arc
        radius,
        startAngle,
        endAngle,
        clockwise
    );
};

export const renderSingleKeyShapePanel = (values: PreviewFormValues) => {
    const leftWidth = Number(values.cabinet_panel_width);
    const rightWidth = Number(values.cabinet_panel_length);
    const leftDepth = Number(values.cabinet_width_depth);
    const rightDepth = Number(values.cabinet_length_depth);
    const carcaseThickness = values.cabinet_carc_thickness;

    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);

    const mesh = generateKeyShapeMesh({
        leftWidth,
        leftDepth,
        rightDepth,
        rightWidth,
        carcaseTexture,
        carcaseEdgeTexture,
        carcaseThickness,
    });

    mesh.rotation.x = Math.PI;
    mesh.scale.y = -1;

    // Key-shape panels sit flush against the wall in the RoomPlanner. Push
    // the panel face slightly back in the depth buffer (polygonOffset) so
    // its outline beats the coplanar wall without disabling depth testing
    // on the line — that avoids the x-ray look.
    applyWallFlushOutlineFix(mesh);
    const lineSegments = createLineSegments(mesh);

    // attach userdata drag state events
    addDragEffectOnClick(
        mesh as unknown as THREE.Mesh<
            THREE.BoxGeometry,
            THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[]
        >,
        lineSegments.material
    );
    // attach actual events
    attachEvents(mesh);
    mesh.add(lineSegments);

    return mesh;
};

export const generateKeyShapeMesh = ({
    leftWidth,
    leftDepth,
    rightDepth,
    rightWidth,
    carcaseTexture,
    carcaseEdgeTexture,
    carcaseThickness,
    horizontalGrainCarc,
    showTexture,
}: {
    leftWidth: number;
    leftDepth: number;
    rightDepth: number;
    rightWidth: number;
    carcaseTexture: THREE.Texture;
    carcaseEdgeTexture: THREE.Texture;
    carcaseThickness: number;
    horizontalGrainCarc?: boolean;
    showTexture?: boolean;
}) => {
    // Create the shape
    const shape = new THREE.Shape();

    // values below are calculated based on ShKeyShapeRev01 workbook
    const straightSectionX = 20;
    const straightSectionY = 20;
    const largeInternalRadiusPercentage = 0.28;

    const minPartSize = min([leftWidth, rightWidth]);
    const minShelfDepth = min([leftDepth, rightDepth]);
    const bigCircleCenterX = minPartSize * 0.5;
    const bigCircleCenterY = minPartSize * 0.5;
    const hDistance = bigCircleCenterX - straightSectionX;
    const dDistance = minPartSize - minShelfDepth - bigCircleCenterX;
    const largeInternalRadius = largeInternalRadiusPercentage * minPartSize;
    const smallExternalRadius =
        (hDistance * hDistance +
            dDistance * dDistance -
            largeInternalRadius * largeInternalRadius) /
        (2 * (largeInternalRadius - dDistance));

    const arcEnd = getArcEnd({
        straightSectionX,
        straightSectionY,
        largeInternalRadiusPercentage,
        minPartSize,
        minShelfDepth,
        bigCircleCenterX,
        bigCircleCenterY,
        hDistance,
        dDistance,
        largeInternalRadius,
        smallExternalRadius,
    });

    // start drawing the panel
    shape.moveTo(0, 0);
    // draw left side
    shape.lineTo(0, leftWidth);
    // draw bottom -- left depth
    shape.lineTo(leftDepth, leftWidth);
    // draw the constant straightSectionY -- starting point of left arc
    shape.lineTo(leftDepth, leftWidth - straightSectionY);

    const leftArcStartX = leftDepth;
    const leftArcStartY = leftWidth - straightSectionY;

    // left arc is located at the bottom, need to mirror arcEnd to get correct coordinates
    const leftArcEndX = rightWidth - arcEnd.x;
    const leftArcEndY = leftWidth - arcEnd.y;

    // get the left arc's proper placement to connect start and end point
    const leftCenter = circleCentersFromPointsAndRadius(
        leftArcStartX,
        leftArcStartY,
        leftArcEndX,
        leftArcEndY,
        smallExternalRadius
    );

    const leftArcCenterX = leftCenter[1]?.x ?? leftDepth - smallExternalRadius;
    const leftArcCenterY = leftCenter[1]?.y ?? leftWidth - straightSectionY;

    drawArc(
        shape,
        smallExternalRadius,
        {x: leftArcCenterX, y: leftArcCenterY},
        {x: leftArcStartX, y: leftArcStartY},
        {x: leftArcEndX, y: leftArcEndY},
        true
    );

    // big arc will start on left arc end
    const bigArcStartX = leftArcEndX;
    const bigArcStartY = leftArcEndY;

    // big arc will end at the inverse of calculated arc end
    const bigArcEndX = rightWidth - arcEnd.y;
    const bigArcEndY = leftWidth - arcEnd.x;

    // big arc center is mirrored at the bottom
    const bigArcCenterX = rightWidth - bigCircleCenterX;
    const bigArcCenterY = leftWidth - bigCircleCenterY;

    drawArc(
        shape,
        largeInternalRadius,
        {x: bigArcCenterX, y: bigArcCenterY},
        {x: bigArcStartX, y: bigArcStartY},
        {x: bigArcEndX, y: bigArcEndY},
        false
    );

    // right arc will start at the end of the big arc
    const rightArcStartX = bigArcEndX;
    const rightArcStartY = bigArcEndY;
    // right arc will end on the straightSectionX coordinate
    const rightArcEndX = rightWidth - straightSectionX;
    const rightArcEndY = rightDepth;

    // get the right arc's proper placement to connect start and end point
    const rightCenter = circleCentersFromPointsAndRadius(
        rightArcStartX,
        rightArcStartY,
        rightArcEndX,
        rightArcEndY,
        smallExternalRadius
    );

    const rightArcCenterX = rightCenter[1]?.x ?? rightWidth - straightSectionX;
    const rightArcCenterY =
        rightCenter[1]?.y ?? rightDepth - smallExternalRadius;

    drawArc(
        shape,
        smallExternalRadius,
        {x: rightArcCenterX, y: rightArcCenterY},
        {x: rightArcStartX, y: rightArcStartY},
        {x: rightArcEndX, y: rightArcEndY},
        true
    );

    // draw the straightSectionX
    shape.lineTo(rightWidth, rightDepth);
    // draw the right depth
    shape.lineTo(rightWidth, 0);
    // draw the right width -- close the shape
    shape.lineTo(0, 0);

    const extrudeSettings = {
        steps: 1,
        depth: carcaseThickness,
        bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    const materials = applyCarcaseMaterial({
        geometry,
        texture: carcaseTexture,
        edgeTexture: carcaseEdgeTexture,
        useSubstrateEdge: false,
        flipGrain: false,
        hasHorizontalGrain: horizontalGrainCarc,
        showTexture,
    });

    // Apply materials to faces
    for (let i = 0; i < geometry.groups.length; i++) {
        geometry.groups[Number(i)].materialIndex = i % 2; // Alternate materials
    }

    // Create the mesh
    return new THREE.Mesh(geometry, materials);
};
